import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Select, Button, Table, List, Progress, Grid, Typography, Modal } from 'antd';
import { marked } from "marked";
import { ExportOutlined, RightOutlined } from '@ant-design/icons';
import { useFindAllEntityMasterMutation, useGetAllEntityMasterBySearchMutation } from '../../redux-toolkit/services/entityMasterService';
import { useGetDashboardDataMutation } from '../../redux-toolkit/services/dashboardService';
import { useDispatch, useSelector } from 'react-redux';
import { getLocalStorageData } from '../../utils/LocalStorageService';
import './dashboard.scss'; // Re-using existing scss or creating new one if needed
import { useFindAllAssetsMutation, useGetAllAssetsBySearchMutation, useGetAllAssetsMutation } from '../../redux-toolkit/services/assetsService';
import { useAppSelector } from '../../redux-toolkit/hooks';
import { allAssetsDetails, getAllAssetsBySearch, getAllAssetsDetails, getAssetsTotalRecords, setAllAssets, setAllAssetsBySearch, setAssets } from '../../redux-toolkit/slices/assetsSlice';
import { alertMessage } from '../../utils/AlertService';
import { OVERVIEW_DASHBOARD_COLUMNS } from '../constant/AppConstant';
import { useGetAllMentorshipMasterMutation } from '../../redux-toolkit/services/mentorshipMasterService';
import { getAllMentorshipMasterDetails, setMentorshipMaster } from '../../redux-toolkit/slices/mentorshipMasterSlice';

const { Option } = Select;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const OverviewDashboard = () => {
    const screens = useBreakpoint();
    const dispatch = useDispatch();
    const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
    const [entities, setEntities] = useState<any[]>([]);
    const [currentpage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [assetsDataSource, setAssetsDataSource] = useState<any[]>([]);

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState<string>("");

    const [getAllAssets, allAssetsResponse] = useGetAllAssetsMutation();

    const [getAllAssetsWithSearch, allAssetsWithSearchResponse] = useGetAllAssetsBySearchMutation();

    const [findAllAssets, findAllAssetsResponse] = useFindAllAssetsMutation();
    const [getAllMentorshipMaster, allMentorshipMasterResponse] = useGetAllMentorshipMasterMutation();

    const totalAssets: any = useAppSelector(getAssetsTotalRecords);
    const allAssetsPaginated: any = useAppSelector(getAllAssetsDetails);
    const allAssetsBySearch: any = useAppSelector(getAllAssetsBySearch);
    const allAssets: any = useAppSelector(allAssetsDetails);
    const allMentorshipMaster: any = useAppSelector(getAllMentorshipMasterDetails);

    const localUserObj: any = getLocalStorageData("localUser");
    const token = localUserObj?.token;

    useEffect(() => {
        if (token) {
            findAllAssets({ token: token });
            getAllAssets({
                token: token,
                page: currentpage - 1,
                size: pageSize,
                sort: "desc",
            });
        }
    }, [token, currentpage, pageSize]);

    useEffect(() => {
        getAllMentorshipMaster({
            token: localUserObj?.token,
            page: 0,
            size: 5,
            orderBy: "desc",
        });
    }, [])

    useEffect(() => {
        if (allAssetsResponse.isSuccess) {
            dispatch(setAssets(allAssetsResponse));
        } else if (allAssetsResponse.isError) {
            let response: any = allAssetsResponse.error;
            alertMessage(response, "error");
        }

    }, [allAssetsResponse.isSuccess, allAssetsResponse.isError]);

    useEffect(() => {
        if (allMentorshipMasterResponse.isSuccess) {
            dispatch(setMentorshipMaster(allMentorshipMasterResponse));
        } else if (allMentorshipMasterResponse.isError) {
            let response: any = allMentorshipMasterResponse.error;
            alertMessage(response, "error");
        }
    }, [allMentorshipMasterResponse.isSuccess, allMentorshipMasterResponse.isError]);

    useEffect(() => {
        if (findAllAssetsResponse.isSuccess) {
            dispatch(setAllAssets(findAllAssetsResponse));
        } else if (findAllAssetsResponse.isError) {
            alertMessage("Error while fetching Assets", "error");
        }
    }, [findAllAssetsResponse.isSuccess, findAllAssetsResponse.isError]);

    const handleSearch = (event: any) => {
        getAllAssetsWithSearch({
            token: localUserObj?.token,
            payload: { offset: 1000, pageNumber: 0, searchBy: event.target.value },
        });
    };

    useEffect(() => {
        if (allAssetsWithSearchResponse.isSuccess) {
            dispatch(setAllAssetsBySearch(allAssetsWithSearchResponse));
        } else if (allAssetsWithSearchResponse.isError) {
            let response: any = allAssetsWithSearchResponse.error;
            alertMessage(response, "error");
        }
    }, [allAssetsWithSearchResponse.isSuccess, allAssetsWithSearchResponse.isError]);

    // API Hooks
    // Using findAll for the dropdown list, assuming reasonable size. 
    // If huge, we'd use search on type.
    const [findAllEntities, { data: allEntitiesData, isLoading: isEntitiesLoading }] = useFindAllEntityMasterMutation();
    const [getDashboardData, { data: dashboardData, isLoading: isDashboardLoading }] = useGetDashboardDataMutation();

    useEffect(() => {
        // Fetch entities on load
        if (token) {
            findAllEntities({ token });
        }
    }, [token]);

    useEffect(() => {
        if (allEntitiesData?.data) {
            setEntities(allEntitiesData.data);
            // Default select first one if none selected
            if (!selectedEntity && allEntitiesData.data.length > 0) {
                setSelectedEntity(allEntitiesData.data[0].id);
            }
        }
    }, [allEntitiesData]);

    useEffect(() => {
        if (selectedEntity && token) {
            getDashboardData({ payload: { entityId: selectedEntity }, token });
        }
    }, [selectedEntity, token]);

    const handleEntityChange = (value: string) => {
        setSelectedEntity(value);
    };

    const buildAssets = (data: any) => {
        let temp: any = [];

        data.forEach((element: any) => {
            temp.push({
                id: element.id,
                entity: element?.entityMaster ? element?.entityMaster.name : "",
                entityType: element?.entityTypeMaster ? element?.entityTypeMaster.name : "",
                health: element?.healthScore,
                llmResponse: element?.llmResponse ? element?.llmResponse : "",
                action: element?.llmResponse ? "View" : "",
            });
        });
        setAssetsDataSource(temp);
    };

    useEffect(() => {
        if (allAssetsPaginated) {
            buildAssets(allAssetsPaginated);
        }
    }, [allAssetsPaginated]);

    // Derived Data (Mocked where API data might be missing structure)
    const currentEntity = entities.find(e => e.id === selectedEntity);
    const entityName = currentEntity?.entityName || 'Select Entity';

    // Mock Data for UI Matching (would map from dashboardData in real implementation)
    const averageHealthScore = React.useMemo(() => {
        if (!allAssets || allAssets.length === 0) return 0;

        const validScores = allAssets
            .map((asset: any) => asset.healthScore)
            .filter((score: number | null) => score !== null && score !== undefined);

        if (validScores.length === 0) return 0;

        const total = validScores.reduce((sum: number, score: number) => sum + score, 0);

        return (total / validScores.length).toFixed(2);
    }, [allAssets]);

    const healthPercentage = React.useMemo(() => {
        if (!averageHealthScore) return 0;
        return Math.round((Number(averageHealthScore) / totalAssets) * 100);
    }, [averageHealthScore]);

    const handleViewReport = (llmResponse: string) => {
        try {
            const response = JSON.parse(llmResponse);
            const finalContent = response?.choices?.[0]?.message?.content || "";
            // marked returns a promise or string depending on options, but in this version sync usage returns string
            const htmlContent = marked.parse(finalContent);
            // In v4+ marked.parse is synchronous if async: false (default), ensuring we await if needed or just use it.
            // TypeScript might complain if marked types aren't fully aligned, casting to any or string helper
            setModalContent(htmlContent as string);
            setIsModalOpen(true);
        } catch (error) {
            console.error("Error parsing LLM response:", error);
            // Fallback for non-JSON or error
            setModalContent(marked.parse(llmResponse) as string);
            setIsModalOpen(true);
        }
    };

    const columns = OVERVIEW_DASHBOARD_COLUMNS.map(col => {
        if (col.dataIndex === 'action') {
            return {
                ...col,
                render: (text: string, record: any) => {
                    if (!text || text === "") return null;
                    return (
                        <a onClick={() => handleViewReport(record.llmResponse)}>View</a>
                    );
                }
            };
        }
        return col;
    });


    const alerts = [
        { id: 1, text: 'Project at risk: Retail OmniPilot (Isha Ambani) — requires attention.', type: 'warning' },
        { id: 2, text: 'Project at risk: Green Hydrogen Pilot (Anant Ambani) — requires attention.', type: 'warning' },
        { id: 3, text: 'Audit Findings: Environmental Review has 3 findings.', type: 'error' }
    ];

    const quickStats = [
        { label: 'Heirs', value: 5, color: '#e6f7ff', textColor: '#1890ff' },
        { label: 'Assets', value: 4, color: '#f6ffed', textColor: '#52c41a' },
        { label: 'Projects', value: 3, color: '#fff7e6', textColor: '#fa8c16' },
        { label: 'Audits', value: 3, color: '#fff1f0', textColor: '#f5222d' },
    ];

    const mentorships = [
        { name: 'Akash Ambani', role: 'Ex-Telco CEO', desc: 'Network scaling', time: '3h' },
        { name: 'Isha Ambani', role: 'Retail Expert', desc: 'Omnichannel', time: '2h' },
        { name: 'Anant Ambani', role: 'Energy Scientist', desc: 'Hydrogen tech', time: '1.5h' },
    ];

    return (
        <div className="p-4" style={{ backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
            {/* Header Section */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Title level={2} style={{ marginBottom: 0 }}>
                        {entityName} — Reliance Family Office Prototype
                    </Title>
                    <Text type="secondary">Single master dashboard • Succession • Wealth • Projects • CAP Table • Audits • Mentorship</Text>
                    <div style={{ marginTop: '8px' }} className="d-flex align-items-center text-muted">
                        <span>Dashboard</span> <RightOutlined style={{ fontSize: '10px', margin: '0 8px' }} /> <span className="text-primary">Overview</span>
                    </div>
                </div>
                <div className="d-flex gap-3">
                    <Select
                        showSearch
                        placeholder="Search across dashboard..."
                        optionFilterProp="children"
                        onChange={handleEntityChange}
                        onSearch={(val) => console.log('search:', val)}
                        filterOption={(input, option) =>
                            (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
                        }
                        style={{ width: 300 }}
                        value={selectedEntity}
                        loading={isEntitiesLoading}
                    >
                        {entities.map(e => (
                            <Option key={e.id} value={e.id}>{e.entityName}</Option>
                        ))}
                    </Select>
                    <Button type="primary" icon={<ExportOutlined />}>Export / Print</Button>
                </div>
            </div>

            {/* Main Content Grid */}
            <Row gutter={[24, 24]}>
                {/* Left/Main Column - 70% */}
                <Col xs={24} lg={16}>
                    {/* Health Score Card */}
                    <Card className="mb-4 shadow-sm" bordered={false} bodyStyle={{ padding: '24px' }}>
                        <Row align="middle">
                            <Col span={16}>
                                <Text type="secondary">Overall Family Health</Text>
                                <div className="d-flex align-items-end">
                                    <Title level={1} style={{ margin: 0, lineHeight: 1 }}>{averageHealthScore}</Title>
                                    <Title level={3} type="secondary" style={{ margin: '0 0 2px 8px', fontWeight: 400 }}> / {totalAssets}</Title>
                                </div>
                                <Text type="secondary">Aggregated {entityName} & Succession Readiness</Text>
                            </Col>
                            <Col span={8}>
                                <div className="text-end">
                                    <Progress
                                        percent={healthPercentage}
                                        status="active"
                                        showInfo={false}
                                        strokeWidth={12}
                                        strokeColor={
                                            healthPercentage >= 80
                                                ? "#52c41a"   // Green
                                                : healthPercentage >= 50
                                                    ? "#faad14"   // Yellow
                                                    : "#f5222d"   // Red
                                        }
                                    />

                                    <Text
                                        strong
                                        type={
                                            healthPercentage >= 80
                                                ? "success"
                                                : healthPercentage >= 50
                                                    ? "warning"
                                                    : "danger"
                                        }
                                    >
                                        {healthPercentage}% healthy
                                    </Text>

                                </div>
                            </Col>
                        </Row>
                    </Card>

                    {/* Key Entities & Quick Actions Layout */}
                    <Row gutter={[24, 24]}>
                        <Col span={14}>
                            <Card title="Key Entities" bordered={false} className="shadow-sm h-100">
                                <Table
                                    dataSource={assetsDataSource}
                                    pagination={{
                                        pageSize: 5,
                                        total: totalAssets,
                                        current: currentpage, // Note: Shared pagination for now as per code structure
                                        onChange: (page, size) => {
                                            setCurrentPage(page);
                                            setPageSize(size);
                                        }
                                    }}
                                    size="small"
                                    columns={columns}
                                />
                            </Card>
                        </Col>
                        <Col span={10}>
                            <Card title="Quick Actions" bordered={false} className="shadow-sm h-100">
                                <List
                                    itemLayout="horizontal"
                                    dataSource={[
                                        'Open Succession Dashboard →',
                                        'Open Projects Dashboard →',
                                        'Open Wealth Dashboard →',
                                        'Export Heirs Data (CSV) →'
                                    ]}
                                    renderItem={item => (
                                        <List.Item>
                                            <a href="#" style={{ fontSize: '15px' }}>{item}</a>
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        </Col>
                    </Row>

                    <div className="mt-4"></div>

                    {/* Recent Mentorship */}
                    <Card title="Recent Mentorship" bordered={false} className="shadow-sm">
                        <List
                            itemLayout="horizontal"
                            dataSource={mentorships}
                            renderItem={item => (
                                <List.Item extra={<Text type="secondary">{item.time}</Text>}>
                                    <List.Item.Meta
                                        title={<Text strong>{item.name}</Text>}
                                        description={
                                            <div>
                                                <Text type="secondary" className="d-block" style={{ fontSize: '12px' }}>{item.role}</Text>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>{item.desc}</Text>
                                            </div>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                    </Card>

                </Col>

                {/* Right/Side Column - 30% */}
                <Col xs={24} lg={8}>
                    {/* Alerts Widget */}
                    <div className="mb-4">
                        <Text strong className="mb-2 d-block">Alerts ({alerts.length})</Text>
                        {alerts.map(alert => (
                            <div key={alert.id} className="p-3 mb-2 rounded border" style={{ backgroundColor: '#fffbe6', borderColor: '#ffe58f' }}>
                                <Text>{alert.text}</Text>
                            </div>
                        ))}
                    </div>

                    {/* Selected Context
                    <Card className="mb-4 shadow-sm" bordered={false}>
                        <div className="d-flex justify-content-between">
                            <Text strong>Selected Context</Text>
                            <Text type="secondary">{entities.find(e => e.id === selectedEntity)?.entityName || 'All'}</Text>
                        </div>
                        <div className="mt-2">
                            <Text type="secondary" style={{ fontSize: '12px' }}>Select an heir, entity, project or audit to view quick details.</Text>
                        </div>
                    </Card> */}

                    {/* Quick Actions (Sidebar)
                    <Card className="mb-4 shadow-sm" title="Quick Actions" bordered={false} size="small">
                        <ul className="list-unstyled mb-0">
                            <li className="mb-2"><span className="text-primary">•</span> Schedule mentorship for low-scoring heirs</li>
                            <li className="mb-2"><span className="text-warning">•</span> Review high risk project mitigation plans</li>
                            <li className="mb-0"><span className="text-danger">•</span> Conduct behavioral audits for at-risk heirs</li>
                        </ul>
                    </Card> */}

                    {/* Quick Stats Grid */}
                    <Card className="mb-4 shadow-sm" title="Quick Stats" bordered={false}>
                        <Row gutter={[12, 12]}>
                            {quickStats.map((stat, idx) => (
                                <Col span={12} key={idx}>
                                    <div className="p-3 text-center rounded" style={{ backgroundColor: stat.color }}>
                                        <Title level={3} style={{ color: stat.textColor, margin: 0 }}>{stat.value}</Title>
                                        <Text style={{ color: stat.textColor }}>{stat.label}</Text>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Card>

                    {/* Recent Alerts List */}
                    <Card title="Recent Alerts" bordered={false} className="shadow-sm">
                        <ul className="list-unstyled mb-0" style={{ fontSize: '12px' }}>
                            {alerts.slice(0, 2).map((alert, idx) => (
                                <li key={idx} className="mb-2">
                                    <span className="text-warning" style={{ marginRight: '8px' }}>●</span>
                                    {alert.text}
                                </li>
                            ))}
                        </ul>
                    </Card>
                </Col>
            </Row>

            <Modal
                title="Risk Assessment Report"
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={[
                    <Button key="close" onClick={() => setIsModalOpen(false)}>
                        Close
                    </Button>
                ]}
                width={800}
                centered
            >
                <div
                    className="p-3"
                    dangerouslySetInnerHTML={{ __html: modalContent }}
                />
            </Modal>
        </div >
    );
};

export default OverviewDashboard;
